import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { hasClassicCounterpart } from "@ugg/shared/utils/multi-build-helpers";
import { SPECIAL_GAME_MODES } from "@ugg/shared/utils/special-game-modes";
import {
  getChampionBuildUrl,
  getClassicChampionBuildUrl,
  getSpecialGameModeChampionBuildUrl,
} from "@ugg/shared/routes/app-routes";

// ─────────────────────────────────────────────────────────────────────
// Resolve the right champion build page for a given queue.
//
// Three URL grammars exist and the choice depends on the queue:
//   • League Classic  /lol/champions/classic/<alias>-classic/<role?>
//   • special modes   /lol/champions/<mode>/<alias>-<mode>
//   • everything else /lol/champions/<alias>/build/<role?>
//
// Classic can't be folded into SPECIAL_GAME_MODES — that map is keyed by
// QueueTypeS and assumes the `<mode>/<alias>-<mode>` grammar plus the
// standard build pipeline, neither of which Classic uses.
//
// This lives in its own module rather than app-routes.ts because
// app-routes is deliberately dependency-light and special-game-modes
// imports *from* it; putting the resolver there would create a cycle.
// ─────────────────────────────────────────────────────────────────────

interface ChampionBuildUrlParams {
  language?: string;
  role?: string;
  // Live u.gg patch, forwarded to hasClassicCounterpart so pre-deployed roster
  // additions resolve here too (see CLASSIC_COUNTERPART_ALIASES_BY_PATCH).
  // Optional because the main caller, ChampLink, is rendered once per champion
  // card in a live game / champ select and has no patch in scope — adding a
  // fetch there to serve a handful of pending aliases isn't worth it. Without
  // it this resolves the core roster only, so a champion in its pre-activation
  // window links to the modern build page instead of the Classic one. That is a
  // soft miss, not a broken link, and it disappears once the aliases are folded
  // into CLASSIC_COUNTERPART_ALIASES.
  patch?: string;
}

export function getChampionBuildUrlForQueue(championAlias: string, queueType?: string, params?: ChampionBuildUrlParams): string {
  const { language, role, patch } = params || {};

  // League Classic. ARAM_MAYHEM_CLASSIC (Riot's KIWI_JADE) routes here too:
  // those games use the Classic champion kits and item set, so the modern
  // build page would be actively wrong. It loses the Mayhem augment module,
  // which is the lesser of the two errors — surfacing augments on the
  // Classic page is a follow-up.
  const isClassicQueue = queueType === QueueTypeS.CLASSIC || queueType === QueueTypeS.ARAM_MAYHEM_CLASSIC;
  if (isClassicQueue && hasClassicCounterpart(championAlias, patch)) {
    return getClassicChampionBuildUrl(championAlias, { language, role });
  }

  const { urlName } = Object.values(SPECIAL_GAME_MODES).find((mode) => mode.queueType === queueType) || {};
  if (urlName) {
    return getSpecialGameModeChampionBuildUrl(championAlias, urlName, { language });
  }

  return getChampionBuildUrl(championAlias, { language, ...(role && { role }) });
}
